import { Component } from 'preact';
import { hsl } from 'd3-color';

import { ABORT_ERROR_NAME, abortRequests, fetchYearlyRainfall } from '../../utils/api';
import {
	addActiveSeries,
	initialState,
	rainfallLoadFailure,
	rainfallLoading,
	rainfallLoadSuccess,
	removeActiveSeries,
	setAllSeriesActive,
	setAllSeriesInactive
} from './reducers';
import YearlyRainGraph from '../../components/yearly-rain-graph';
import CheckboxIcon from '../../components/icons/checkbox';
import style from './style.scss';
import PageTitle from '../../components/page-title';
import { EVENT_ACTION, EVENT_CATEGORY } from '../../utils/tracking';

/**
 * The HistoryRainfallPage component is a route component, controlling the rainfall history view.
 */
class HistoryRainfallPage extends Component {

	constructor(props, context) {
		super(props, context);

		this.state = initialState();

		this.colors = null;

		this.loadRainfall = this.loadRainfall.bind(this);
		this.onToggle = this.onToggle.bind(this);
		this.selectAllSeries = this.selectAllSeries.bind(this);
		this.deselectAllSeries = this.deselectAllSeries.bind(this);

		this.requestAbort = {
			rainfall: null
		};
	}

	loadRainfall() {
		this.setState(rainfallLoading());

		const params = {
			years: 'all',
			leapYearMerge: 1,
			average: 1
		};

		const request = fetchYearlyRainfall(params);
		this.requestAbort.rainfall = request.abort;

		request.promise
			.then(res => {
				const hueStep = 360 / res.data.years.length;
				this.colors = res.data.years.map((d, i) => hsl(i * hueStep, 1, 0.4).toString());

				this.setState(rainfallLoadSuccess(res.data));
			})
			.catch(err => {
				if (err.name !== ABORT_ERROR_NAME) {
					this.setState(state => rainfallLoadFailure(state.rainfallRetryCount));
					this.context.tracking.recordException('Error loading yearly rainfall data', err);
				}
			});
	}

	componentDidMount() {
		this.loadRainfall();

		this.context.titleService.update('Rainfall History');
		this.context.tracking.recordPageView();
	}

	componentWillUnmount() {
		abortRequests(this.requestAbort);
	}

	isSeriesActive(name) {
		return this.state.activeSeries.indexOf(name) !== -1;
	}

	onToggle(evt) {
		if (evt.type === 'keyup' && evt.keyCode !== 13) {
			return;
		}

		let node = evt.target;

		while (!node.hasAttribute('data-series')) {
			node = node.parentNode;
		}

		const seriesName = node.getAttribute('data-series');
		const isActive = this.isSeriesActive(seriesName);

		if (isActive) {
			this.setState(state => removeActiveSeries(state.activeSeries, seriesName));
		} else {
			this.setState(state => addActiveSeries(state.activeSeries, seriesName));
		}

		this.context.tracking.recordEvent(
			isActive ? EVENT_ACTION.HISTORY_RAINFALL_HIDE_YEAR : EVENT_ACTION.HISTORY_RAINFALL_SHOW_YEAR,
			EVENT_CATEGORY.HISTORY_RAINFALL,
			`series: ${seriesName}`
		);
	}

	selectAllSeries() {
		this.setState(state => setAllSeriesActive(state.rainfall));
	}

	deselectAllSeries() {
		this.setState(() => setAllSeriesInactive());
	}

	renderToggleForm() {
		if (!this.state.rainfall) {
			return null;
		}

		return (
			<form class={style.form}>
				<div class={style.year_option_wrapper}>
					<div class={style.year_option_average}
					     data-series="average"
					     onClick={this.onToggle}
					     onKeyUp={this.onToggle}
					     role="checkbox"
					     tabIndex={0}
					     aria-checked={this.isSeriesActive('average')}>

						<CheckboxIcon checked={this.isSeriesActive('average')} />
						<span class={style.year_option_label}>Mean</span>

					</div>
				</div>
				{
					this.state.rainfall.years.map((yearData, index) => {
						return (
							<div class={style.year_option_wrapper}>
								<div class={style.year_option}
								     data-series={yearData.year}
								     onClick={this.onToggle}
								     onKeyUp={this.onToggle}
								     role="checkbox"
								     tabIndex={0}
								     aria-checked={this.isSeriesActive(`${yearData.year}`)}>

									<CheckboxIcon fill={this.colors[index]} checked={this.isSeriesActive(`${yearData.year}`)} />
									<span class={style.year_option_label}>{ yearData.year }</span>
								</div>
							</div>
						);
					}).reverse()
				}
			</form>
		);
	}

	render() {
		return (
			<div>
				<PageTitle>Yearly Rainfall</PageTitle>

				<p>
					The graph below displays yearly rainfall accumulating daily recorded by the weather station.
					The bold black line shows the average (mean) for comparison. Note that for leap years, any rainfall on
					the 29th of February is combined with the 28th.
				</p>

				<YearlyRainGraph data={this.state.rainfall}
				                 rainUnit={this.context.userSettings.units.rain}
				                 activeSeries={this.state.activeSeries}
				                 colors={this.colors}
				                 isLoading={this.state.rainfallLoading}
				                 retryCount={this.state.rainfallRetryCount}
				                 errorMessage="Unable to load yearly rainfall data"
				                 retryHandler={this.loadRainfall} />

				{ this.renderToggleForm() }


				<div className={style.select_deselect_btns}>
					<button onClick={this.selectAllSeries}>Select all
					</button>
					<button onClick={this.deselectAllSeries}>Deselect all
					</button>
				</div>
			</div>
		);
	}

}

export default HistoryRainfallPage;



// WEBPACK FOOTER //
// ./routes/history-rainfall/index.js